<template>
  <div class="page flex-col">
    <AppHeader />

    <!-- 顶部 Banner -->
    <div class="pd-banner">
      <div class="pd-banner-mask">
        <span class="pd-banner-zh">{{ sectionName }}</span>
        <span class="pd-banner-en">{{ sectionNameEn }}</span>
      </div>
    </div>

    <div class="pd-main">
      <!-- 面包屑 -->
      <nav class="pd-breadcrumb">
        <router-link to="/">首页</router-link>
        <span class="pd-sep">&gt;</span>
        <router-link :to="listLink">{{ sectionName }}</router-link>
        <span class="pd-sep">&gt;</span>
        <router-link :to="listLink">{{ categoryLabel }}</router-link>
        <span class="pd-sep">&gt;</span>
        <span class="pd-crumb-active">{{ product.model || product.name }}</span>
      </nav>

      <!-- 页标题 -->
      <div class="pd-title-row">
        <span class="pd-title-zh">{{ categoryLabel || sectionName }}</span>
        <span class="pd-title-en">PRODUCTS</span>
      </div>
      <div class="pd-title-line"></div>

      <!-- 侧栏 + 详情 -->
      <div class="pd-body">
        <aside class="pd-sidebar">
          <button
            v-for="(cat, index) in categories"
            :key="cat.id || 'cat_' + index"
            type="button"
            class="pd-side-item"
            :class="{ active: cat.id === activeCategoryId }"
            @click="onCategory(cat)"
          >
            {{ cat.name }}
          </button>
        </aside>

        <div class="pd-content">
          <h1 class="pd-product-name">{{ product.name }}</h1>

          <div v-if="featureList.length" class="pd-block">
            <div class="pd-block-label">工艺特点：</div>
            <ul class="pd-feature-list">
              <li v-for="(item, index) in featureList" :key="'ft_' + index">{{ item }}</li>
            </ul>
          </div>

          <div v-if="product.application" class="pd-block">
            <div class="pd-block-label">应用范围：</div>
            <p class="pd-app-text">{{ product.application }}</p>
          </div>

          <div class="pd-hero-img-wrap">
            <img
              class="pd-hero-img"
              :src="productImage"
              :alt="product.name"
            />
          </div>

          <template v-if="displayGuns.length">
            <h2 class="pd-gun-title">{{ relatedTitle }}</h2>
            <div class="pd-gun-grid">
              <div
                v-for="(gun, index) in displayGuns"
                :key="gun.id || 'gun_' + index"
                class="pd-gun-card"
              >
                <div class="pd-gun-img-wrap">
                  <img class="pd-gun-img" :src="gun.image" :alt="gun.name" />
                </div>
                <div class="pd-gun-name">{{ gun.name }}</div>
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>

    <AppFooter />
  </div>
</template>

<script>
const defaultHeroImg = require('./assets/img/FigmaDDSSlicePNG2a0fb788776a372de3c8c890e2a77ed7.png');
const defaultGunImg = require('./assets/img/FigmaDDSSlicePNG0b3e1899bcc0a26372d88e668647a49c.png');

const fallbackProduct = {
  model: 'HR-HVAF-GUN-A',
  name: 'HR-HVAF-GUN-A',
  section: 'guns',
  category: 'hvaf-gun',
  featureList: [
    '低温可控，加热与加速分离，减少材料过热；',
    '高速度，700-1000m/s，涂层致密，孔隙率可小于0.5%；',
    '氧化物含量低，接近真空喷涂水平；',
    '结合强度高，与碳钢的结合强度大于75MPa；',
    '低成本，空气助燃，无需制氧，运行费用低；',
    '压缩空气冷却枪体，无需水冷，维护方便。'
  ],
  application:
    '适合工厂现场，移动施工，外场维修，尤其适合大面积、长周期工业维修。广泛应用于煤炭化工、石油钻井、纺织机械、造纸行业等。',
  relatedGuns: [
    { id: 'hr-hvaf-1702id', name: 'HR-HVAF-1702ID' },
    { id: 'hr-hvaf-1706', name: 'HR-HVAF-1706' }
  ]
};

export default {
  name: 'ProductDetailGun',
  data() {
    return {
      sections: [],
      detailsMap: {},
      fallbackProduct
    };
  },
  computed: {
    productId() {
      return this.$route.query.id || 'hr-hvaf-gun-a';
    },
    activeSectionId() {
      return this.$route.query.section || this.product.section || 'guns';
    },
    activeCategoryId() {
      return this.$route.query.cat || this.product.category || '';
    },
    activeSection() {
      return this.sections.find((s) => s.id === this.activeSectionId) || null;
    },
    sectionName() {
      return (this.activeSection && this.activeSection.name) || '喷枪';
    },
    sectionNameEn() {
      return (this.activeSection && this.activeSection.nameEn) || 'Spray guns';
    },
    categories() {
      return (this.activeSection && this.activeSection.categories) || [];
    },
    activeCategory() {
      return this.categories.find((c) => c.id === this.activeCategoryId) || this.categories[0] || null;
    },
    categoryLabel() {
      if (!this.activeCategory) return '';
      return this.activeCategory.label || this.activeCategory.name;
    },
    listLink() {
      return {
        path: '/products/catalog',
        query: {
          section: this.activeSectionId,
          cat: this.activeCategoryId || undefined
        }
      };
    },
    product() {
      const fromMap = this.detailsMap[this.productId];
      if (fromMap) return this.enrichProduct(fromMap);
      if (this.detailsMap.default) {
        const base = this.enrichProduct(this.detailsMap.default);
        const listName = this.findProductName(this.productId);
        return {
          ...base,
          name: listName || base.name,
          model: listName || base.model,
          section: this.$route.query.section || 'guns',
          category: this.$route.query.cat || base.category
        };
      }
      return this.fallbackProduct;
    },
    featureList() {
      if (Array.isArray(this.product.featureList) && this.product.featureList.length) {
        return this.product.featureList;
      }
      if (this.product.features) {
        return String(this.product.features)
          .split(/[；;]/)
          .map((s) => s.trim())
          .filter(Boolean)
          .map((s) => (s.endsWith('；') || s.endsWith(';') ? s : `${s}；`));
      }
      return [];
    },
    productImage() {
      return this.product.image || defaultHeroImg;
    },
    relatedTitle() {
      return this.product.relatedTitle || this.categoryLabel || '相关喷枪';
    },
    displayGuns() {
      const related = this.product.relatedGuns || [];
      return related.map((gun) => ({
        ...gun,
        image: gun.image || defaultGunImg
      }));
    }
  },
  created() {
    this.fetchDeviceData();
  },
  methods: {
    async fetchDeviceData() {
      try {
        const res = await fetch('/data/device.json');
        if (!res.ok) return;
        const data = await res.json();
        this.sections = data.sections || [];
        this.detailsMap = data.details || {};
      } catch (err) {
        console.warn('Failed to fetch device.json:', err);
      }
    },
    findProductName(id) {
      for (const section of this.sections) {
        for (const cat of section.categories || []) {
          const found = (cat.products || []).find((p) => p.id === id);
          if (found) return found.name;
        }
      }
      return '';
    },
    enrichProduct(detail) {
      return {
        ...detail,
        section: detail.section || this.$route.query.section || 'guns',
        category: detail.category || this.$route.query.cat || ''
      };
    },
    onCategory(cat) {
      if (!cat || !cat.id) return;
      this.$router.push({
        path: '/products/catalog',
        query: {
          section: this.activeSectionId,
          cat: cat.id
        }
      });
    }
  }
};
</script>

<style scoped>
.page {
  width: 100%;
  max-width: 1920px;
  margin: 0 auto;
  background: #fff;
  display: flex;
  flex-direction: column;
  align-items: center;
  box-sizing: border-box;
  overflow-x: hidden;
}

/* 顶部 Banner */
.pd-banner {
  width: 100%;
  height: 360px;
  background: url(./assets/img/FigmaDDSSlicePNG4bfbddd95358d0de5c2a6890b148e575.jpg)
    center center / cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.pd-banner::after {
  content: '';
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.28);
}

.pd-banner-mask {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.pd-banner-zh {
  color: #fff;
  font-size: 44px;
  font-weight: 900;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.25);
}

.pd-banner-en {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
}

.pd-main {
  width: 1550px;
  max-width: 92%;
  margin: 0 auto;
  padding: 28px 0 72px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pd-breadcrumb {
  width: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 14px;
  color: #6b7280;
  margin-bottom: 24px;
}

.pd-breadcrumb a {
  color: #6b7280;
  text-decoration: none;
}

.pd-breadcrumb a:hover {
  color: #1e40af;
}

.pd-sep {
  color: #9ca3af;
}

.pd-crumb-active {
  color: #374151;
}

.pd-title-row {
  display: flex;
  align-items: baseline;
  gap: 14px;
}

.pd-title-zh {
  font-size: 32px;
  font-weight: 900;
  color: #1e40af;
  line-height: 1.2;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-title-en {
  font-size: 18px;
  font-weight: 600;
  color: #94a3b8;
  letter-spacing: 1px;
}

.pd-title-line {
  width: 100%;
  height: 2px;
  background: #93c5fd;
  margin: 16px 0 32px;
}

.pd-body {
  width: 100%;
  display: grid;
  grid-template-columns: 260px 1fr;
  gap: 40px;
  align-items: start;
}

.pd-sidebar {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.pd-side-item {
  width: 100%;
  border: none;
  outline: none;
  cursor: pointer;
  text-align: left;
  padding: 18px 20px;
  border-radius: 6px;
  font-size: 15px;
  font-weight: 600;
  line-height: 1.4;
  color: #1e40af;
  background: #dbeafe;
  transition: background 0.2s ease, color 0.2s ease;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-side-item:hover {
  background: #bfdbfe;
}

.pd-side-item.active {
  background: #1e40af;
  color: #fff;
}

.pd-content {
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.pd-product-name {
  margin: 0 0 20px;
  font-size: 26px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-block {
  width: 100%;
  margin-bottom: 22px;
}

.pd-block-label {
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 10px;
}

.pd-feature-list {
  margin: 0;
  padding-left: 1.4em;
  color: #4b5563;
  font-size: 14px;
  line-height: 1.9;
}

.pd-feature-list li {
  margin-bottom: 2px;
}

.pd-app-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.9;
  color: #4b5563;
  text-indent: 2em;
}

.pd-hero-img-wrap {
  width: 100%;
  margin-top: 12px;
  display: flex;
  justify-content: center;
  background: #f8fafc;
  border-radius: 8px;
  padding: 28px 20px;
  box-sizing: border-box;
}

.pd-hero-img {
  max-width: 420px;
  width: 100%;
  height: auto;
  object-fit: contain;
}

.pd-gun-title {
  margin: 40px 0 20px;
  font-size: 26px;
  font-weight: 800;
  color: #1e40af;
  line-height: 1.3;
  width: 100%;
  text-align: left;
  font-family: "PingFang SC", "Microsoft YaHei", sans-serif;
}

.pd-gun-grid {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 28px;
}

.pd-gun-card {
  background: #0a0a0a;
  border-radius: 10px;
  padding: 40px 24px 28px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 24px;
}

.pd-gun-img-wrap {
  width: 100%;
  height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.pd-gun-img {
  max-width: 90%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  display: block;
}

.pd-gun-name {
  font-size: 18px;
  font-weight: 700;
  color: #60a5fa;
  text-align: center;
}

@media (max-width: 1100px) {
  .pd-body {
    grid-template-columns: 1fr;
  }

  .pd-sidebar {
    flex-direction: row;
    flex-wrap: wrap;
  }

  .pd-side-item {
    width: auto;
    flex: 1 1 200px;
  }
}

@media (max-width: 700px) {
  .pd-gun-grid {
    grid-template-columns: 1fr;
  }

  .pd-title-zh {
    font-size: 26px;
  }
}
</style>
